import { defineStore } from 'pinia'

export type ContactKind =
  | 'email'
  | 'phone'
  | 'linkedin'
  | 'github'
  | 'instagram'
  | 'twitter'
  | 'facebook'
  | 'whatsapp'
  | 'telegram'

export interface ContactItem {
  id: string
  platformId: number
  key: string
  displayValue?: string
  href: string
  icon: ContactKind
  external: boolean
}

interface RawContact {
  id?: string | number
  key?: string
  label?: string
  value?: string
  link?: string
  url?: string
  platform_id?: number | string
  platformId?: number | string
  platform_value?: number | string
  platformValue?: number | string
  platform?: number | string | {
    id?: number | string
    value?: number | string
  }
}

interface Platform {
  id: number
  label: string
  kind: ContactKind
  showValue: boolean
}

const platforms: Platform[] = [
  { id: 1, label: 'ایمیل', kind: 'email', showValue: true },
  { id: 2, label: 'تلفن', kind: 'phone', showValue: true },
  { id: 3, label: 'لینکدین', kind: 'linkedin', showValue: false },
  { id: 4, label: 'گیت‌هاب', kind: 'github', showValue: false },
  { id: 5, label: 'اینستاگرام', kind: 'instagram', showValue: false },
  { id: 6, label: 'توییتر', kind: 'twitter', showValue: false },
  { id: 7, label: 'فیسبوک', kind: 'facebook', showValue: false },
  { id: 8, label: 'واتساپ', kind: 'whatsapp', showValue: true },
  { id: 94, label: 'تلگرام', kind: 'telegram', showValue: false }
]

const platformById = new Map(platforms.map(platform => [platform.id, platform]))

export const contactIconPaths: Record<ContactKind, string> = {
  email: 'M3.5 6h17v12h-17zM3.5 6l8.5 6.5L20.5 6',
  phone: 'M7.5 3.5h3l1.4 3.6-2 1.4a10 10 0 004.6 4.6l1.4-2 3.6 1.4v3a2 2 0 01-2.2 2A15.5 15.5 0 015.5 5.7 2 2 0 017.5 3.5z',
  linkedin: 'M4.5 9.5h3V20h-3zM6 4.5a1.6 1.6 0 100 3.2 1.6 1.6 0 000-3.2zM10.5 20V9.5h3v1.6c.6-1 1.7-1.8 3.3-1.8 2.3 0 3.7 1.5 3.7 4.4V20h-3v-5.9c0-1.4-.6-2.3-1.9-2.3-1.2 0-2.1.9-2.1 2.3V20z',
  github: 'M12 2.5a9.5 9.5 0 00-3 18.5c.5.1.6-.2.6-.5v-1.7c-2.6.6-3.2-1.2-3.2-1.2-.4-1.1-1-1.4-1-1.4-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.3 1.1 2.9.8.1-.6.3-1.1.6-1.3-2.1-.2-4.3-1-4.3-4.6 0-1 .4-1.9 1-2.5-.1-.3-.4-1.3.1-2.6 0 0 .8-.3 2.6 1a9 9 0 014.8 0c1.8-1.3 2.6-1 2.6-1 .5 1.3.2 2.3.1 2.6.6.6 1 1.5 1 2.5 0 3.6-2.2 4.4-4.3 4.6.3.3.6.9.6 1.8v2.6c0 .3.2.6.7.5A9.5 9.5 0 0012 2.5z',
  instagram: 'M7.5 3h9A4.5 4.5 0 0121 7.5v9A4.5 4.5 0 0116.5 21h-9A4.5 4.5 0 013 16.5v-9A4.5 4.5 0 017.5 3z',
  twitter: 'M18.9 3h3.7l-8.1 9.3L24 21h-7.4l-5.8-6.1L5.5 21H1.8l7.7-8.8L1 3h7.6l5.2 5.5z',
  facebook: 'M14.5 21v-8h2.7l.4-3h-3.1V8.1c0-.9.3-1.6 1.7-1.6h1.8V3.8c-.3 0-1.4-.1-2.6-.1-2.6 0-4.4 1.6-4.4 4.5V10H8v3h3v8z',
  whatsapp: 'M12.04 2a9.84 9.84 0 00-8.4 14.97L2.25 22l5.16-1.35A9.86 9.86 0 1012.04 2zm0 17.95a8.1 8.1 0 01-4.13-1.13l-.3-.18-3.06.8.82-2.98-.2-.31a8.12 8.12 0 1114.99-4.3 8.12 8.12 0 01-8.12 8.1zm4.45-6.08c-.24-.12-1.44-.71-1.67-.8-.22-.08-.38-.12-.55.13-.16.24-.63.79-.77.95-.14.16-.28.18-.53.06-.24-.12-1.03-.38-1.96-1.21a7.4 7.4 0 01-1.36-1.69c-.14-.24-.01-.37.11-.5.11-.1.24-.28.36-.42.13-.14.17-.24.25-.4.08-.17.04-.31-.02-.43-.06-.12-.55-1.32-.75-1.81-.2-.48-.4-.42-.55-.42h-.47c-.16 0-.43.06-.65.3-.23.25-.86.84-.86 2.04 0 1.2.88 2.36 1 2.52.12.16 1.72 2.63 4.17 3.69.58.25 1.03.4 1.39.51.58.19 1.11.16 1.53.1.47-.07 1.45-.59 1.65-1.16.2-.57.2-1.06.14-1.16-.06-.1-.22-.16-.46-.28z',
  telegram: 'M21 4.5L2.9 11.2l5 1.7 1.7 5.6 2.8-3.6 4.9 3.6zM7.9 12.9L21 4.5l-8.6 10.4'
}

export const filledContactIcons: ContactKind[] = [
  'linkedin',
  'github',
  'twitter',
  'facebook',
  'whatsapp'
]

function getPlatformId(contact: RawContact) {
  const nestedPlatform = typeof contact.platform === 'object'
    ? contact.platform
    : undefined
  const candidate = contact.platform_id
    ?? contact.platformId
    ?? contact.platform_value
    ?? contact.platformValue
    ?? nestedPlatform?.id
    ?? nestedPlatform?.value
    ?? contact.platform
  const id = Number(candidate)

  return Number.isFinite(id) ? id : undefined
}

function normalizeDigits(value: string) {
  return value
    .replace(/[۰-۹]/g, digit => String(digit.charCodeAt(0) - 1776))
    .replace(/[٠-٩]/g, digit => String(digit.charCodeAt(0) - 1632))
}

function phoneHref(value: string) {
  const normalized = normalizeDigits(value).trim()
  const hasPlus = normalized.startsWith('+')
  const digits = normalized.replace(/\D/g, '')

  return digits ? `tel:${hasPlus ? '+' : ''}${digits}` : ''
}

function whatsappHref(value: string) {
  if (/^https?:\/\//i.test(value)) return value
  if (/^(wa\.me|api\.whatsapp\.com)\//i.test(value)) return `https://${value}`

  let digits = normalizeDigits(value).replace(/\D/g, '')
  if (/^09\d{9}$/.test(digits)) digits = `98${digits.slice(1)}`

  return digits ? `https://wa.me/${digits}` : ''
}

function externalHref(kind: ContactKind, value: string) {
  if (/^https?:\/\//i.test(value)) return value
  if (/^(www\.)?(linkedin\.com|github\.com|instagram\.com|x\.com|twitter\.com|facebook\.com|t\.me)\//i.test(value)) {
    return `https://${value}`
  }

  const username = value.replace(/^@/, '').replace(/^\/+|\/+$/g, '')
  const baseUrls: Partial<Record<ContactKind, string>> = {
    linkedin: 'https://linkedin.com/in/',
    github: 'https://github.com/',
    instagram: 'https://instagram.com/',
    twitter: 'https://x.com/',
    facebook: 'https://facebook.com/',
    telegram: 'https://t.me/'
  }

  return baseUrls[kind] ? `${baseUrls[kind]}${username}` : ''
}

export const useMyContactsStore = defineStore('myContactsStore', () => {
  const portfolioStore = usePortfolioStore()

  const contacts = computed<ContactItem[]>(() => {
    const source = (portfolioStore.contacts ?? []) as RawContact[]

    return source.flatMap((contact, index) => {
      const platformId = getPlatformId(contact)
      const platform = platformId ? platformById.get(platformId) : undefined
      const value = (contact.url ?? contact.link ?? contact.value)?.trim()
      if (!platform || !value) return []

      const href = platform.kind === 'email'
        ? (/^mailto:/i.test(value) ? value : `mailto:${value}`)
        : platform.kind === 'phone'
          ? phoneHref(value)
          : platform.kind === 'whatsapp'
            ? whatsappHref(value)
            : externalHref(platform.kind, value)

      if (!href) return []

      return [{
        id: String(contact.id ?? `${platform.id}-${index}`),
        platformId: platform.id,
        key: contact.key?.trim() || contact.label?.trim() || platform.label,
        displayValue: platform.showValue
          ? value.replace(/^(mailto:|tel:)/i, '')
          : undefined,
        href,
        icon: platform.kind,
        external: platform.kind !== 'email' && platform.kind !== 'phone'
      }]
    })
  })

  return { contacts }
})
